import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/contexts/AuthContext";
import logoFull from "@/assets/logo-full.png";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register(name, email, password);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background noise-bg flex items-center justify-center px-6">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <Link to="/" className="block mb-8 w-fit mx-auto" data-testid="brand-logo-full">
          <img src={logoFull} alt="Angiò A.I. — Dalla tua idea al tuo sito web" className="w-60 h-60 object-contain rounded-3xl border border-white/10 shadow-2xl shadow-primary/5" />
        </Link>
        <h1 className="text-3xl font-display font-bold text-center">Crea il tuo account</h1>
        <p className="text-muted-foreground mt-2 text-sm text-center">Gratis. Bastano pochi secondi.</p>

        <form onSubmit={submit} className="mt-8 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Nome</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Mario Rossi"
              required
              className="bg-white/5 border-white/10 h-11"
              data-testid="register-name-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="tu@esempio.com"
              required
              className="bg-white/5 border-white/10 h-11"
              data-testid="register-email-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Almeno 6 caratteri"
              required
              minLength={6}
              className="bg-white/5 border-white/10 h-11"
              data-testid="register-password-input"
            />
          </div>
          {error && (
            <p className="text-sm text-destructive" data-testid="register-error">
              {error}
            </p>
          )}
          <Button
            type="submit"
            disabled={loading}
            className="w-full h-11 bg-primary text-black hover:bg-[#33F3FF] font-semibold"
            data-testid="register-submit-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Registrati"}
          </Button>
        </form>

        <p className="text-sm text-muted-foreground mt-6 text-center">
          Hai già un account?{" "}
          <Link to="/login" className="text-primary hover:underline" data-testid="goto-login">
            Accedi
          </Link>
        </p>
      </motion.div>
    </div>
  );
}
